"use client"

import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import "../styles/BasicLinkedList.css"
import Button from "../../shared/Button"

function BasicLinkedList({ addLog }) {
  const [nodes, setNodes] = useState([])
  const [inputValue, setInputValue] = useState("")
  const [inputPosition, setInputPosition] = useState("")
  const [isAnimating, setIsAnimating] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(null)
  const [animationSpeed, setAnimationSpeed] = useState(500)
  const [currentOperation, setCurrentOperation] = useState(null)
  const [searchValue, setSearchValue] = useState("")
  const [searchResult, setSearchResult] = useState(null)
  const [showIntro, setShowIntro] = useState(true)

  const chainContainerRef = useRef(null)

  // Generate a random color for chain links
  const getRandomColor = () => {
    const colors = ["#FF6B6B", "#4ECDC4", "#FFD166", "#06D6A0", "#118AB2", "#EF476F", "#FFC43D", "#1B9AAA"]
    return colors[Math.floor(Math.random() * colors.length)]
  }

  // Scroll to the end of the chain when nodes change
  useEffect(() => {
    if (chainContainerRef.current) {
      chainContainerRef.current.scrollLeft = chainContainerRef.current.scrollWidth
    }
  }, [nodes])

  // Add a node at the beginning of the list
  const handleAddAtBeginning = () => {
    if (inputValue.trim() === "") return
    if (isAnimating) return

    setIsAnimating(true)
    setCurrentOperation("addBeginning")
    setShowIntro(false)

    const newNode = {
      id: Date.now(),
      value: inputValue,
      color: getRandomColor(),
      isNew: true,
    }

    addLog(`Adding "${inputValue}" at the beginning of the chain`, "success")

    // First show the new node above the chain
    setNodes((prevNodes) => [{ ...newNode, isFloating: true }, ...prevNodes])

    // Then after a delay, place it at the beginning
    setTimeout(() => {
      setNodes((prevNodes) => {
        const updatedNodes = prevNodes.map((node, index) =>
          index === 0 ? { ...node, isFloating: false, isNew: false } : node,
        )
        return updatedNodes
      })

      setTimeout(() => {
        setIsAnimating(false)
        setCurrentOperation(null)
        setInputValue("")
      }, animationSpeed)
    }, animationSpeed)
  }

  // Add a node at the end of the list
  const handleAddAtEnd = () => {
    if (inputValue.trim() === "") return
    if (isAnimating) return

    setIsAnimating(true)
    setCurrentOperation("addEnd")
    setShowIntro(false)

    const newNode = {
      id: Date.now(),
      value: inputValue,
      color: getRandomColor(),
      isNew: true,
    }

    addLog(`Adding "${inputValue}" at the end of the chain`, "success")

    // Highlight each node in sequence to simulate traversal
    const traverseNodes = async () => {
      for (let i = 0; i < nodes.length; i++) {
        setHighlightedIndex(i)
        await new Promise((resolve) => setTimeout(resolve, animationSpeed / 2))
      }
      setHighlightedIndex(null)

      // Add the new node
      setNodes((prevNodes) => [...prevNodes, { ...newNode, isFloating: true }])

      // Then place it at the end
      setTimeout(() => {
        setNodes((prevNodes) => {
          const updatedNodes = [...prevNodes]
          updatedNodes[updatedNodes.length - 1] = {
            ...updatedNodes[updatedNodes.length - 1],
            isFloating: false,
            isNew: false,
          }
          return updatedNodes
        })

        setTimeout(() => {
          setIsAnimating(false)
          setCurrentOperation(null)
          setInputValue("")
        }, animationSpeed)
      }, animationSpeed)
    }

    if (nodes.length > 0) {
      traverseNodes()
    } else {
      // If list is empty, just add the node
      setNodes([{ ...newNode, isFloating: true }])

      setTimeout(() => {
        setNodes((prevNodes) => {
          const updatedNodes = prevNodes.map((node) => ({ ...node, isFloating: false, isNew: false }))
          return updatedNodes
        })

        setTimeout(() => {
          setIsAnimating(false)
          setCurrentOperation(null)
          setInputValue("")
        }, animationSpeed)
      }, animationSpeed)
    }
  }

  // Add a node at a specific position
  const handleAddAtPosition = () => {
    if (inputValue.trim() === "" || inputPosition.trim() === "") return
    if (isAnimating) return

    const position = Number.parseInt(inputPosition)
    if (isNaN(position) || position < 0 || position > nodes.length) {
      addLog(`Invalid position: ${inputPosition}. Must be between 0 and ${nodes.length}`, "error")
      return
    }

    setIsAnimating(true)
    setCurrentOperation("addPosition")
    setShowIntro(false)

    const newNode = {
      id: Date.now(),
      value: inputValue,
      color: getRandomColor(),
      isNew: true,
    }

    addLog(`Adding "${inputValue}" at position ${position} of the chain`, "success")

    // Highlight each node up to the position to simulate traversal
    const traverseNodes = async () => {
      for (let i = 0; i < position; i++) {
        setHighlightedIndex(i)
        await new Promise((resolve) => setTimeout(resolve, animationSpeed / 2))
      }
      setHighlightedIndex(null)

      // Insert the new node
      setNodes((prevNodes) => {
        const updatedNodes = [...prevNodes]
        updatedNodes.splice(position, 0, { ...newNode, isFloating: true })
        return updatedNodes
      })

      // Then place it in position
      setTimeout(() => {
        setNodes((prevNodes) => {
          const updatedNodes = [...prevNodes]
          updatedNodes[position] = {
            ...updatedNodes[position],
            isFloating: false,
            isNew: false,
          }
          return updatedNodes
        })

        setTimeout(() => {
          setIsAnimating(false)
          setCurrentOperation(null)
          setInputValue("")
          setInputPosition("")
        }, animationSpeed)
      }, animationSpeed)
    }

    traverseNodes()
  }

  // Delete a node at a specific position
  const handleDelete = () => {
    if (inputPosition.trim() === "") return
    if (isAnimating) return
    if (nodes.length === 0) {
      addLog("Cannot delete from an empty list", "error")
      return
    }

    const position = Number.parseInt(inputPosition)
    if (isNaN(position) || position < 0 || position >= nodes.length) {
      addLog(`Invalid position: ${inputPosition}. Must be between 0 and ${nodes.length - 1}`, "error")
      return
    }

    setIsAnimating(true)
    setCurrentOperation("delete")
    setShowIntro(false)

    addLog(`Deleting node at position ${position} from the chain`, "info")

    // Highlight each node up to the position to simulate traversal
    const traverseNodes = async () => {
      for (let i = 0; i < position; i++) {
        setHighlightedIndex(i)
        await new Promise((resolve) => setTimeout(resolve, animationSpeed / 2))
      }

      // Highlight the node to be deleted
      setHighlightedIndex(position)
      await new Promise((resolve) => setTimeout(resolve, animationSpeed))

      // Mark the node as being deleted (floating away)
      setNodes((prevNodes) => {
        const updatedNodes = [...prevNodes]
        updatedNodes[position] = { ...updatedNodes[position], isDeleting: true }
        return updatedNodes
      })

      // After animation, remove the node
      setTimeout(() => {
        setNodes((prevNodes) => {
          const updatedNodes = [...prevNodes]
          updatedNodes.splice(position, 1)
          return updatedNodes
        })

        setHighlightedIndex(null)
        setIsAnimating(false)
        setCurrentOperation(null)
        setInputPosition("")
      }, animationSpeed)
    }

    traverseNodes()
  }

  // Search for a node with a specific value
  const handleSearch = () => {
    if (searchValue.trim() === "") return
    if (isAnimating) return
    if (nodes.length === 0) {
      addLog("Cannot search in an empty list", "error")
      return
    }

    setIsAnimating(true)
    setCurrentOperation("search")
    setSearchResult(null)
    setShowIntro(false)

    addLog(`Searching for "${searchValue}" in the chain`, "info")

    // Simulate traversal and search
    const searchNodes = async () => {
      let found = false

      for (let i = 0; i < nodes.length; i++) {
        setHighlightedIndex(i)
        await new Promise((resolve) => setTimeout(resolve, animationSpeed / 2))

        if (nodes[i].value.toLowerCase() === searchValue.toLowerCase()) {
          found = true
          setSearchResult({ index: i, value: searchValue, found: true })
          addLog(`Found "${searchValue}" at position ${i}`, "success")
          break
        }
      }

      if (!found) {
        setSearchResult({ found: false, value: searchValue })
        addLog(`"${searchValue}" not found in the list`, "warning")
      }

      setTimeout(() => {
        setHighlightedIndex(null)
        setIsAnimating(false)
        setCurrentOperation(null)
        setSearchValue("")
      }, animationSpeed)
    }

    searchNodes()
  }

  // Clear the entire list
  const handleClear = () => {
    if (nodes.length === 0) return

    setNodes([])
    setHighlightedIndex(null)
    setSearchResult(null)
    addLog("Cleared the entire chain", "warning")
  }

  // Handle the introduction tutorial
  const handleStartTutorial = () => {
    setShowIntro(false)
    addLog("Welcome to the Linked List Visualizer! Try adding some nodes to the chain.", "info")
  }

  return (
    <div className="basic-linked-list">
      {showIntro && (
        <div className="intro-overlay">
          <div className="intro-content">
            <h2>Welcome to Linked List Visualizer!</h2>
            <p>
              A linked list is a fundamental data structure that consists of nodes where each node contains data and a
              reference to the next node in the sequence.
            </p>
            <div className="intro-diagram">
              <div className="intro-node">
                <div className="intro-data">Data</div>
                <div className="intro-next">Next</div>
                <div className="intro-arrow">→</div>
              </div>
              <div className="intro-node">
                <div className="intro-data">Data</div>
                <div className="intro-next">Next</div>
                <div className="intro-arrow">→</div>
              </div>
              <div className="intro-node">
                <div className="intro-data">Data</div>
                <div className="intro-next">Next</div>
                <div className="intro-arrow">→</div>
              </div>
              <div className="intro-node">
                <div className="intro-data">Data</div>
                <div className="intro-next">NULL</div>
              </div>
            </div>
            <p>
              In this visualizer, we'll represent a linked list as a chain where each link connects to the next one.
              You'll be able to:
            </p>
            <ul>
              <li>Add links to the beginning, end, or at a specific position</li>
              <li>Delete links from any position</li>
              <li>Search for specific values in the chain</li>
            </ul>
            <Button variant="success" size="lg" onClick={handleStartTutorial}>Start Exploring</Button>
          </div>
        </div>
      )}

      <div className="list-controls">
        <div className="input-group">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Enter a value"
            disabled={isAnimating}
            className="node-input"
          />

          <div className="position-input">
            <input
              type="text"
              value={inputPosition}
              onChange={(e) => setInputPosition(e.target.value)}
              placeholder="Position (optional)"
              disabled={isAnimating}
              className="position-input"
            />
          </div>

          <div className="operation-buttons">
            <Button size="sm" onClick={handleAddAtBeginning} disabled={isAnimating || inputValue.trim() === ""}>Add at Beginning</Button>

            <Button size="sm" onClick={handleAddAtEnd} disabled={isAnimating || inputValue.trim() === ""}>Add at End</Button>

            <Button size="sm" onClick={handleAddAtPosition} disabled={isAnimating || inputValue.trim() === "" || inputPosition.trim() === ""}>Add at Position</Button>

            <Button variant="danger" size="sm" onClick={handleDelete} disabled={isAnimating || inputPosition.trim() === "" || nodes.length === 0}>Delete at Position</Button>
          </div>
        </div>

        <div className="search-group">
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search value"
            disabled={isAnimating}
            className="search-input"
          />

          <Button variant="ghost" size="sm" onClick={handleSearch} disabled={isAnimating || searchValue.trim() === "" || nodes.length === 0}>Search</Button>

          <Button variant="danger" size="sm" onClick={handleClear} disabled={isAnimating || nodes.length === 0}>Clear List</Button>
        </div>
      </div>

      <div className="chain-visualization">
        <div className="chain-container" ref={chainContainerRef}>
          {nodes.length === 0 ? (
            <div className="empty-list-message">The chain is empty. Add some links!</div>
          ) : (
            <div className="chain">
              <AnimatePresence>
                {nodes.map((node, index) => (
                  <motion.div
                    key={node.id}
                    className={`chain-link ${highlightedIndex === index ? "highlighted" : ""} ${node.isNew ? "new-node" : ""}`}
                    style={{ backgroundColor: node.color }}
                    initial={node.isFloating ? { y: -50, opacity: 0.7 } : { y: 0, opacity: 1 }}
                    animate={
                      node.isDeleting
                        ? { y: -100, opacity: 0, transition: { duration: 0.5 } }
                        : node.isFloating
                          ? { y: -50, opacity: 0.7 }
                          : { y: 0, opacity: 1 }
                    }
                    exit={{ y: -100, opacity: 0, transition: { duration: 0.5 } }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="link-body">
                      <div className="link-value">{node.value}</div>
                      <div className="link-index">{index}</div>
                      {searchResult && searchResult.found && searchResult.index === index && (
                        <div className="search-indicator">Found!</div>
                      )}
                    </div>
                    <div className="link-connector">
                      <div className="connector-line"></div>
                      <div className="connector-arrow">→</div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              <div className="null-terminator">NULL</div>
            </div>
          )}
        </div>

        {searchResult && searchResult.found === false && (
          <div className="search-not-found">Value "{searchResult.value}" not found in the chain</div>
        )}
      </div>

      <div className="list-info">
        <div className="list-status">
          <div className="status-item">
            <span className="status-label">Length:</span>
            <span className="status-value">{nodes.length}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Head:</span>
            <span className="status-value">{nodes.length > 0 ? nodes[0].value : "null"}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Tail:</span>
            <span className="status-value">{nodes.length > 0 ? nodes[nodes.length - 1].value : "null"}</span>
          </div>
        </div>

        <div className="animation-speed">
          <label>Animation Speed:</label>
          <input
            type="range"
            min="200"
            max="1000"
            step="100"
            value={animationSpeed}
            onChange={(e) => setAnimationSpeed(Number(e.target.value))}
            disabled={isAnimating}
          />
          <span>{animationSpeed}ms</span>
        </div>
      </div>
    </div>
  )
}

export default BasicLinkedList
